<template>
	<!-- ************************************************************************** Data table -->
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between">
				<WTitle text="Units" />
				<!-- ************************************************************************** Add new button -->
				<PButton
					@click="showAddNewOrEdit = true"
					icon="pi pi-plus"
					rounded
					size="small"
					v-tooltip.left="`Add a new measurement unit`"
				></PButton>
			</div>
		</template>
		<template #content>
			<PDataTable
				:alwaysShowPaginator="false"
				:highlightOnSelect="true"
				:loading="loading"
				:resizableColumns="true"
				:rowHover="true"
				:rows="100"
				:showGridlines="true"
				:sortOrder="1"
				:stripedRows="true"
				:value="list"
				data-key="id"
				paginator
				size="small"
			>
				<template #empty>No records found.</template>

				<PColumn
					bodyClass="text-left"
					class="justify-content-center"
					field="unit"
					header="Unit"
					headerClass="py-3"
					sortable
					style="width: 120px;"
				></PColumn>
				<PColumn
					bodyClass="text-left text-wrap"
					field="description"
					header="Description"
					headerClass="py-3"
					sortable
				></PColumn>
				<PColumn
					bodyClass="text-right"
					headerClass="py-3"
					style="width: 100px;"
				>
					<template #body="{ data }">
						<PButton
							@click="doEdit(data)"
							class="p-button-xs"
							icon="pi pi-pencil"
							rounded
							severity="success"
							text
							v-tooltip.bottom="`Edit`"
						></PButton>
						<PButton
							@click="doDelete($event, data.id)"
							icon="pi pi-trash"
							rounded
							severity="danger"
							size="small"
							text
							v-tooltip.bottom="`Delete`"
						></PButton>
					</template>
				</PColumn>
			</PDataTable>
		</template>
	</PCard>

	<!-- ************************************************************************** Add/Edit dialog -->
	<PDialog
		:header="`${editing? 'Edit' : 'Add a new'}`"
		@hide="cancelAddOrEdit()"
		modal
		v-model:visible="showAddNewOrEdit"
	>
		<div class="flex flex-column gap-4">
			<div class="flex flex-column gap-2">
				<label
					class="text-primary-50"
					for="unit"
				>Unit</label>
				<PInputText
					autocomplete="off"
					id="unit"
					size="small"
					v-model="item.unit"
				></PInputText>
			</div>
			<div class="flex flex-column gap-2">
				<label
					class="text-primary-50"
					for="description"
				>Description</label>
				<PInputText
					autocomplete="off"
					id="description"
					size="small"
					v-model="item.description"
				></PInputText>
			</div>
			<div class="flex align-items-center justify-content-end gap-3">
				<PButton
					@click="cancelAddOrEdit()"
					label="Cancel"
					outlined
					severity="secondary"
					size="small"
				></PButton>
				<PButton
					@click="editing? saveUpdates() : doAdd()"
					label="Save"
					size="small"
				></PButton>
			</div>
		</div>
	</PDialog>

	<!-- ************************************************************************** Delete confirmation dialog -->
	<PConfirmDialog />
</template>

<script lang="ts">
	import { defineComponent, Events } from 'vue'
	import BEBroker from '@/brokers/BEBroker'
	import Utils from '@/utils/Utils'
	import Constants from '@/helpers/Constants'

	export default defineComponent({
		name: 'UnitsView',
		components: {
		},

		data() {
			return {
				utils: Utils,
				component: 'units',
				loading: true,
				showAddNewOrEdit: false,
				editing: false,
				list: [],
				item: {
					id: -1,
					unit: '',
					description: '',
				},
			}
		},

		async mounted() {
			Utils.generatePid(Constants.OPID.READ)
			await this.refreshList()
			this.loading = false
		},

		methods: {
			/**
			 * Refresh the items list
			 */
			async refreshList() {
				const res = await BEBroker.get(this.component)
				if (res != null && Object.keys(res).length !== 0) {
					this.list = res
				} else {
					this.list = []
				}
			},

			/**
			 * add a new item
			 */
			async doAdd() {
				// Validate input
				if (this.item.unit.trim() === '' || this.item.description.trim() === '') {
					Utils.showErrorMessage('All fields are required.')
					return
				}

				this.loading = true
				Utils.generatePid(Constants.OPID.ADD)
				const payload = {
					unit: this.item.unit.trim(),
					description: this.item.description.trim(),
				}
				const res = await BEBroker.add(this.component, payload)
				if (res) {
					this.showAddNewOrEdit = false
					this.refreshList()
					Utils.showSuccessMessage('Data has been added successfully.')
					Utils.clearObject(this.item)
				} else {
					Utils.showErrorMessage('Error while adding data!')
				}
				this.loading = false
			},

			/**
			 * Show edit dialog
			 */
			doEdit(data: any) {
				this.editing = true
				this.item.id = data.id
				this.item.unit = data.unit
				this.item.description = data.description
				this.showAddNewOrEdit = true
			},


			/**
			 * Close add or edit dialog and clear all fields
			 */
			cancelAddOrEdit() {
				this.showAddNewOrEdit = false
				this.editing = false
				Utils.clearObject(this.item)
			},


			/**
			 * Save the updated record
			*/
			async saveUpdates() {
				// Validate input
				if (this.item.unit.trim() === '' || this.item.description.trim() === '') {
					Utils.showErrorMessage('All fields are required.')
					return
				}

				this.loading = true
				Utils.generatePid(Constants.OPID.UPDATE)
				const payload = {
					id: this.item.id,
					unit: this.item.unit.trim(),
					description: this.item.description.trim(),
				}
				this.showAddNewOrEdit = false
				const res = await BEBroker.update(this.component, payload)
				this.editing = false
				Utils.clearObject(this.item)
				this.refreshList()
				this.loading = false
				Utils.showSuccessMessage('Data has been updated successfully.')
			},


			/**
			 * Delete record
			 */
			async doDelete(event: any, id: number) {
				this.$confirm.require({
					target: event.currentTarget,
					header: 'Delete',
					message: 'Are you sure you want to delete this record?',
					icon: 'pi pi-info-circle',
					rejectClass: 'p-button-primary p-button-outlined p-button-sm',
					acceptClass: 'p-button-danger p-button-sm',
					rejectLabel: 'Cancel',
					acceptLabel: 'Delete',
					accept: async () => {
						this.loading = true
						Utils.generatePid(Constants.OPID.DELETE)
						const res = await BEBroker.delete(this.component, id)
						this.refreshList()
						this.loading = false
						Utils.showSuccessMessage('Data has been deleted successfully.')
					},
					reject: () => {
						// Do nothing
					}
				})
			},


		}
	})
</script>
